import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { chatModeType } from '../../types/Chat/Chat.types';

type ChatContextType = {
    mode: chatModeType;
    conversationId?: string;
    reset: boolean;
    load: boolean;
    hasFile: boolean;
    isPending: boolean;
    isStream: boolean;
    setConversationId(id: string | undefined): void;
    setMode(mode: chatModeType): void;
    setReset(reset: boolean): void;
    setLoad(load: boolean): void;
    setHasFile(hasFile: boolean): void;
    setIsPending(isPending: boolean): void;
    setIsStream(isStream: boolean): void;
};
const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const defaultChatMode: chatModeType = 'logistics';

const defaultChatContext: Omit<
    ChatContextType,
    'setConversationId' | 'setMode' | 'setReset' | 'setLoad' | 'setHasFile' | 'setIsPending' | 'setIsStream'
> = {
    mode: defaultChatMode,
    conversationId: undefined,
    reset: false,
    load: false,
    hasFile: false,
    isPending: false,
    isStream: false,
};

export const ChatProvider: React.FC<React.PropsWithChildren<Record<string, unknown>>> = ({ children }) => {
    const [chatContext, setChatContext] = useState(defaultChatContext);

    const setMode = useCallback((mode: chatModeType) => {
        setChatContext((prev) => ({ ...prev, mode }));
    }, []);

    const setConversationId = useCallback((id: string | undefined) => {
        setChatContext((prev) => ({ ...prev, conversationId: id }));
    }, []);

    const setReset = useCallback((reset: boolean) => {
        setChatContext((prev) => ({ ...prev, reset }));
    }, []);

    const setLoad = useCallback((load: boolean) => {
        setChatContext((prev) => ({ ...prev, load }));
    }, []);

    const setHasFile = useCallback((hasFile: boolean) => {
        setChatContext((prev) => ({ ...prev, hasFile }));
    }, []);

    const setIsPending = useCallback((isPending: boolean) => {
        setChatContext((prev) => ({ ...prev, isPending }));
    }, []);

    const setIsStream = useCallback((isStream: boolean) => {
        setChatContext((prev) => ({ ...prev, isStream }));
    }, []);

    const contextValue = useMemo(
        () => ({
            ...chatContext,
            setMode,
            setConversationId,
            setReset,
            setLoad,
            setHasFile,
            setIsPending,
            setIsStream,
        }),
        [chatContext, setMode, setConversationId, setReset, setLoad, setHasFile, setIsPending, setIsStream],
    );

    return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChatContext must be used within a ChatProvider.');
    }
    return context;
};
